import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { STAGE_DOT } from "@/components/StageBadge";
import {
  ArrowLeft,
  Camera,
  CaretRight,
  ChatText,
  CheckCircle,
  ClipboardText,
  DownloadSimple,
  UploadSimple,
  Drop,
  EnvelopeSimple,
  Files,
  MapPin,
  NavigationArrow,
  PaperPlaneTilt,
  Clock,
  PencilSimple,
  Phone,
  Plus,
  Trash,
  User,
  X,
  CalendarBlank,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { crewLabel, usePermissions, canReschedule } from "@/lib/permissions";
import { PhotoDeleteDialog } from "@/components/PhotoDeleteDialog";
import { directionsUrl } from "@/pages/mobile/MobileStop";
import { ConfirmTextDialog } from "@/components/ConfirmTextDialog";
import { JobEmailDialog } from "@/components/JobEmailDialog";
import { ReservedPieces } from "@/components/ReservedPieces";
import { MobileQuoteDrawing } from "@/components/MobileQuoteDrawing";
import { RescheduleSheet } from "@/components/RescheduleSheet";

const WF_STOPS = ["Template", "Fabrication", "Install"];
function resolveStopKind(job, kind) {
  if (WF_STOPS.includes(kind)) return kind;
  if (WF_STOPS.includes(job?.stage)) return job.stage;
  if (job?.template_date) return "Template";
  if (job?.fab_date) return "Fabrication";
  if (job?.install_date) return "Install";
  return "Install";
}

const TABS = [
  ["overview", "Overview", ClipboardText],
  ["project", "Project", MapPin],
  ["comms", "Comms", ChatText],
  ["docs", "Docs", Files],
  ["photos", "Photos", Camera],
  ["fixtures", "Fixtures", Drop],
];

const renderQuick = (body, first, shop) =>
  (body || "")
    .replaceAll("{name}", first || "there")
    .replaceAll("{first}", first || "")
    .replaceAll("{shop}", shop || "our shop")
    .replace(/\s{2,}/g, " ")
    .trim();

const FIXTURE_FIELDS = [
  ["sink", "Sink / fixture", "Kitchen sink"],
  ["area", "Room / area", "Kitchen"],
  ["piece", "Piece", "vanity"],
  ["mount", "Mount", "undermount"],
  ["faucet_holes", "Faucet holes", '3 (8" spread)'],
  ["accessory_holes", "Accessory holes", 'soap pump'],
  ["notes", "Notes", "any special instructions"],
];
const blankFixture = () => ({
  sink: "",
  area: "",
  piece: "",
  mount: "",
  faucet_holes: "",
  accessory_holes: "",
  notes: "",
});

const token = () => localStorage.getItem("sw_token");
const photoUrl = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/photos/${path}?auth=${token()}`;
const fileUrl = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token()}`;
const livePhotos = (job) => (job?.photos || []).filter((p) => !p.is_deleted);

const fmtBytes = (b) => {
  if (b == null || Number.isNaN(Number(b))) return "";
  const n = Number(b);
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const fileTone = (ext) =>
  ext === "pdf"
    ? "border-red-500/30 bg-red-500/10 text-red-300"
    : ["jpg", "jpeg", "png", "webp", "heic", "gif"].includes(ext)
      ? "border-purple-500/30 bg-purple-500/10 text-purple-300"
      : ["xls", "xlsx", "csv"].includes(ext)
        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
        : "border-zinc-700 bg-[#0F172A] text-zinc-300";
const shortTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const Card = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-zinc-800 bg-[#0F172A] p-4"
  >
    {children}
  </div>
);

const Row = ({ label, value, testId }) =>
  value ? (
    <div className="flex justify-between gap-4 py-2" data-testid={testId}>
      <span className="shrink-0 text-xs uppercase tracking-wide text-zinc-500">
        {label}
      </span>
      <span className="min-w-0 break-words text-right text-sm text-zinc-200">
        {value}
      </span>
    </div>
  ) : null;

export default function MobileJob() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [emailDlg, setEmailDlg] = useState(false);
  const [emails, setEmails] = useState([]);
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyFile, setBusyFile] = useState(false);
  const [capId, setCapId] = useState("");
  const [delId, setDelId] = useState("");
  const { can } = usePermissions();
  const { role } = usePermissions();
  const canDelete = can("delete:photos");
  const [capText, setCapText] = useState("");
  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [texting, setTexting] = useState(false);
  const [omwConfirm, setOmwConfirm] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const [fxEdit, setFxEdit] = useState(null);
  const [fxBusy, setFxBusy] = useState(false);
  const [pieces, setPieces] = useState([]);
  const photoRef = useRef(null);
  const galleryRef = useRef(null);
  const fileRef = useRef(null);
  const custId = job?.customer_id || "";

  useEffect(() => {
    api
      .get(`/jobs/${id}/pieces`)
      .then(({ data }) => setPieces(data.items || []))
      .catch(() => setPieces([]));
  }, [id, job?.updated_at]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/jobs/${id}`)
      .then(({ data }) => {
        if (!alive) return;
        setJob(data);
        if (data.customer_id)
          api
            .get(`/customers/${data.customer_id}`)
            .then((r) => alive && setCustomer(r.data))
            .catch(() => {});
      })
      .catch(() => alive && setJob(false))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  const loadFiles = useCallback(() => {
    api
      .get(`/jobs/${id}/files`)
      .then(({ data }) =>
        setFiles(Array.isArray(data) ? data : data.items || data.files || []),
      )
      .catch(() => {});
  }, [id]);
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const loadMessages = useCallback(async () => {
    const reqs = [
      api.get(`/sms/messages`, { params: { entity: "job", entity_id: id } }),
    ];
    if (custId)
      reqs.push(
        api.get(`/sms/messages`, {
          params: { entity: "customer", entity_id: custId },
        }),
      );
    try {
      const res = await Promise.all(reqs);
      const merged = {};
      res.forEach(({ data }) =>
        (data.items || []).forEach((m) => {
          merged[m.id] = m;
        }),
      );
      const list = Object.values(merged).sort((a, b) =>
        (a.sent_at || "").localeCompare(b.sent_at || ""),
      );
      setMessages(list);
    } catch {
      /* ignore */
    }
  }, [id, custId]);
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);
  const loadEmails = useCallback(() => {
    api
      .get(`/jobs/${id}/emails`)
      .then(({ data }) => setEmails(data.items || []))
      .catch(() => {});
  }, [id]);
  useEffect(() => {
    loadEmails();
  }, [loadEmails]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [shopName, setShopName] = useState("");
  useEffect(() => {
    api
      .get("/sms/quick-replies")
      .then(({ data }) => {
        setQuickReplies(data.items || []);
        setShopName(data.shop_name || "");
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (tab !== "comms" || !custId) return;
    api
      .post(`/sms/read`, null, {
        params: { entity: "customer", entity_id: custId },
      })
      .then(() =>
        setMessages((ms) =>
          ms.map((m) => (m.direction === "inbound" ? { ...m, read: true } : m)),
        ),
      )
      .catch(() => {});
  }, [tab, custId]);

  const openOnWay = async () => {
    try {
      const { data } = await api.get(`/jobs/${id}/on-my-way/preview`);
      if (!data.to) {
        toast.error("No customer phone on file");
        return;
      }
      setOmwConfirm({ to: data.to, message: data.message });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not load the text",
      );
    }
  };

  const doTextOnWay = async (message) => {
    setTexting(true);
    try {
      await api.post(`/jobs/${id}/on-my-way`, { message });
      toast.success("On-the-way text sent");
      setOmwConfirm(null);
      loadMessages();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send text",
      );
    }
    setTexting(false);
  };

  const sendText = async () => {
    const phone = customer?.phone || customer?.secondary_phone || "";
    if (!phone) {
      toast.error("No customer phone on file");
      return;
    }
    const body = msgText.trim();
    if (!body) return;
    setMsgBusy(true);
    try {
      await api.post("/sms/send", {
        to: phone,
        body,
        entity: "job",
        entity_id: id,
      });
      setMsgText("");
      toast.success("Text sent");
      loadMessages();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send text",
      );
    }
    setMsgBusy(false);
  };

  const sendQuick = async (body) => {
    const phone = customer?.phone || customer?.secondary_phone || "";
    if (!phone) {
      toast.error("No customer phone on file");
      return;
    }
    setMsgBusy(true);
    try {
      await api.post("/sms/send", { to: phone, body, entity: "job", entity_id: id });
      toast.success("Text sent");
      loadMessages();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not send text",
      );
    }
    setMsgBusy(false);
  };

  const addNote = async () => {
    const body = note.trim();
    if (!body) return;
    setNoteBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/notes`, { body });
      setJob(data);
      setNote("");
      toast.success("Note added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not add note",
      );
    }
    setNoteBusy(false);
  };

  const markComplete = async () => {
    const kind = resolveStopKind(job, sp.get("kind"));
    setCompleting(true);
    try {
      const { data } = await api.patch(`/jobs/${id}/stops/${kind}`, { status: "Done" });
      setJob(data);
      toast.success("Task marked complete");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not update task",
      );
    }
    setCompleting(false);
  };

  const saveFixture = async () => {
    const payload = {
      sink: fxEdit.sink || "",
      area: fxEdit.area || "",
      piece: fxEdit.piece || "",
      mount: fxEdit.mount || "",
      faucet_holes: fxEdit.faucet_holes || "",
      accessory_holes: fxEdit.accessory_holes || "",
      notes: fxEdit.notes || "",
      tags: fxEdit.tags || [],
      warning: fxEdit.warning || "",
      in_stock: fxEdit.in_stock !== false,
    };
    setFxBusy(true);
    try {
      const { data } = fxEdit.id
        ? await api.put(`/jobs/${id}/fixtures/${fxEdit.id}`, payload)
        : await api.post(`/jobs/${id}/fixtures`, payload);
      setJob(data);
      setFxEdit(null);
      toast.success(fxEdit.id ? "Fixture updated" : "Fixture added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save fixture",
      );
    }
    setFxBusy(false);
  };

  const deleteFixture = async (fid) => {
    try {
      const { data } = await api.delete(`/jobs/${id}/fixtures/${fid}`);
      setJob(data);
      toast.success("Fixture removed");
    } catch {
      toast.error("Could not remove fixture");
    }
  };

  const onPhoto = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setUploading(true);
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/jobs/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob(data);
      toast.success("Photo added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
    setUploading(false);
  };

  const removePhoto = async (pid) => {
    try {
      const { data } = await api.delete(`/jobs/${id}/photos/${pid}`);
      setJob(data);
      if (capId === pid) setCapId("");
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const saveCaption = async () => {
    const pid = capId;
    if (!pid) return;
    try {
      const { data } = await api.patch(`/jobs/${id}/photos/${pid}`, {
        caption: capText,
      });
      setJob(data);
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    }
    setCapId("");
  };

  const onFile = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setBusyFile(true);
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/jobs/${id}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      loadFiles();
      toast.success("File uploaded");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
    setBusyFile(false);
  };

  const removeFile = async (fid) => {
    try {
      await api.delete(`/jobs/${id}/files/${fid}`);
      loadFiles();
      toast.success("File removed");
    } catch {
      toast.error("Could not remove file");
    }
  };

  if (loading)
    return (
      <div className="p-6 text-center text-sm text-zinc-500" data-testid="mobile-job-loading">
        Loading job…
      </div>
    );
  if (!job)
    return (
      <div className="p-6 text-center" data-testid="mobile-job-missing">
        <p className="text-sm text-zinc-400">This job could not be found.</p>
        <Link
          to="/mobile/calendar"
          className="mt-4 inline-flex rounded-xl border border-zinc-700 px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-200"
        >
          Back to calendar
        </Link>
      </div>
    );

  const photos = livePhotos(job);
  const fixtures = job.fixtures || [];
  const address = job.address || customer?.job_addresses?.[0] || customer?.main_address || "";
  const phone = customer?.phone || customer?.secondary_phone || "";
  const email = customer?.email || "";
  const stopKind = resolveStopKind(job, sp.get("kind"));
  const isComplete = job.stop_status?.[stopKind]?.status === "Done";
  const isFab = stopKind === "Fabrication";
  const reschedulePrefix = { Template: "template", Fabrication: "fab", Install: "install" }[stopKind];
  const rescheduleCurrent = reschedulePrefix
    ? {
        date: job[`${reschedulePrefix}_date`] || "",
        window: job[`${reschedulePrefix}_window`] || "All day",
        time: job[`${reschedulePrefix}_time`] || "",
      }
    : { date: "", window: "All day", time: "" };
  const hasUnread = messages.some((m) => m.direction === "inbound" && m.read === false);
  const firstName = (customer?.name || job.customer_name || "").split(" ")[0];

  return (
    <div className="min-h-full" data-testid="mobile-job">
      {/* Sub-header */}
      <div className="sticky top-0 z-30 border-b border-zinc-800 bg-[#080C14]/95 px-4 py-3 backdrop-blur">
        <button
          type="button"
          data-testid="mobile-job-back"
          onClick={() => navigate(-1)}
          className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-400 hover:text-[#c39e6d]"
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
        <div className="flex items-center gap-2">
          <span className="font-mono-data text-xs text-zinc-400">{job.code}</span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-[#0F172A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-zinc-300`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${STAGE_DOT[job.stage] || "bg-zinc-500"}`}
            />
            {job.stage || "—"}
          </span>
        </div>
        <h1 className="mt-1.5 text-base font-bold leading-tight text-zinc-50">
          {job.project_name || "Job"}
        </h1>
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">
          {customer?.company || job.customer_name || customer?.name || "—"}
        </div>
      </div>

      {/* Tab bar */}
      <div
        className="flex gap-2 overflow-x-auto border-b border-zinc-800/70 bg-[#080C14]/95 px-3 py-3 backdrop-blur [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="mobile-job-tabs"
      >
        {(isFab ? TABS.filter(([k]) => k !== "photos") : TABS).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            data-testid={`mobile-job-tab-${key}`}
            onClick={() => setTab(key)}
            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-[11px] font-bold uppercase tracking-wide transition-all duration-200 active:scale-95 ${
              tab === key
                ? "bg-zinc-100 text-zinc-950 shadow-[0_6px_16px_-8px_rgba(255,255,255,0.35)]"
                : "bg-[#0F172A]/80 text-zinc-400 ring-1 ring-inset ring-zinc-800 hover:text-zinc-200 hover:ring-zinc-700"
            }`}
          >
            <span className="relative">
              <Icon size={15} weight="bold" />
              {key === "comms" && hasUnread && tab !== "comms" && (
                <span
                  data-testid="mobile-job-comms-unread"
                  className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-zinc-950"
                />
              )}
            </span>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-4 p-4">
        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            {/* Mini map hero */}
            <div
              className="relative overflow-hidden rounded-3xl border border-[#c39e6d]/25 bg-gradient-to-b from-[#c39e6d]/10 via-zinc-950 to-zinc-950"
              style={{ aspectRatio: "2 / 1" }}
              data-testid="mobile-job-map"
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(99,102,241,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.18) 1px, transparent 1px)",
                  backgroundSize: "30px 30px",
                }}
              />
              <span className="absolute left-3 top-3 rounded-lg bg-[#080C14]/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#c39e6d] ring-1 ring-white/5">
                You → Job site
              </span>
              <svg
                className="absolute inset-0 h-full w-full"
                viewBox="0 0 100 50"
                preserveAspectRatio="none"
              >
                <line
                  x1="18"
                  y1="38"
                  x2="66"
                  y2="18"
                  stroke="#818cf8"
                  strokeWidth="0.8"
                  strokeDasharray="2.4 2"
                  strokeLinecap="round"
                  opacity="0.85"
                />
              </svg>
              <span
                className="absolute h-3.5 w-3.5 rounded-full bg-blue-500 ring-2 ring-zinc-950"
                style={{ left: "18%", top: "76%", transform: "translate(-50%,-50%)" }}
              />
              <span
                className="absolute flex h-9 w-9 items-center justify-center rounded-full bg-[#c39e6d] text-zinc-950 shadow-[0_0_20px_-2px_rgba(99,102,241,0.9)] ring-2 ring-zinc-950"
                style={{ left: "66%", top: "36%", transform: "translate(-50%,-50%)" }}
              >
                <MapPin size={16} weight="fill" />
              </span>
              {address && !isFab && (
                <a
                  href={directionsUrl(address)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mobile-job-directions"
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-xl border border-zinc-700 bg-[#080C14]/80 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-100 backdrop-blur hover:bg-[#0F172A]"
                >
                  <NavigationArrow size={14} weight="bold" /> Navigate
                </a>
              )}
            </div>

            {/* Complete */}
            <PhotoDeleteDialog
              open={!!delId}
              onOpenChange={(o) => !o && setDelId("")}
              onConfirm={async () => {
                await removePhoto(delId);
                setDelId("");
              }}
              testId="mobile-job-photo-delete-dialog"
            />
            <ConfirmTextDialog
              open={!!omwConfirm}
              onOpenChange={(o) => !o && setOmwConfirm(null)}
              to={omwConfirm?.to}
              firstName={firstName}
              message={omwConfirm?.message || ""}
              title="On my way — review text"
              sending={texting}
              onSend={doTextOnWay}
            />
            <JobEmailDialog
              open={emailDlg}
              onOpenChange={setEmailDlg}
              jobId={id}
              onSent={loadEmails}
            />
            {job.appt_confirmed_at && (
              <div
                data-testid="mobile-job-confirmed"
                className="flex items-center justify-center gap-2 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-[12px] font-bold uppercase tracking-widest text-emerald-300"
              >
                <CheckCircle size={16} weight="fill" /> Customer confirmed by text
              </div>
            )}
            {isComplete ? (
              <div
                data-testid="mobile-job-complete-done"
                className="flex h-14 items-center justify-center gap-2 rounded-2xl border border-emerald-500/40 bg-emerald-500/15 text-sm font-bold uppercase tracking-widest text-emerald-300"
              >
                <CheckCircle size={18} weight="fill" /> {stopKind} complete
              </div>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="mobile-job-complete"
                  onClick={markComplete}
                  disabled={completing}
                  className="m-display flex h-14 w-full items-center justify-center gap-3 rounded-2xl border border-emerald-500/60 bg-emerald-500/15 text-xl text-emerald-200 transition-colors duration-150 hover:bg-emerald-500/25 disabled:opacity-40"
                >
                  <CheckCircle size={22} weight="bold" />
                  {completing ? "Saving…" : `Mark ${stopKind.toLowerCase()} complete`}
                </button>
                {photos.length === 0 && !isFab && (
                  <p
                    data-testid="mobile-job-complete-hint"
                    className="text-center text-[11px] text-zinc-500"
                  >
                    A job photo is recommended, but optional.
                  </p>
                )}
              </>
            )}

            {canReschedule(role) && ["Template", "Fabrication", "Install"].includes(stopKind) && (
              <button
                type="button"
                data-testid="mobile-job-reschedule"
                onClick={() => setRescheduleOpen(true)}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-[#c39e6d]/50 bg-[#c39e6d]/10 text-[12px] font-bold uppercase tracking-widest text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d]/20"
              >
                <CalendarBlank size={17} weight="bold" /> Reschedule {stopKind.toLowerCase()}
              </button>
            )}
            <RescheduleSheet
              open={rescheduleOpen}
              onOpenChange={setRescheduleOpen}
              jobId={id}
              kind={stopKind}
              current={rescheduleCurrent}
              onDone={(data) => setJob(data)}
            />

            {/* Contact */}
            <Card testId="mobile-job-overview">
              <div className="flex items-center gap-2.5">
                <User size={17} weight="bold" className="shrink-0 text-zinc-500" />
                <span className="text-[15px] font-bold text-zinc-100">
                  {customer?.name || job.customer_name || "—"}
                </span>
              </div>
              {address && (
                <div className="mt-2.5 flex items-start gap-2.5 text-[14px] text-zinc-300">
                  <MapPin size={16} weight="bold" className="mt-0.5 shrink-0 text-[#c39e6d]" />
                  <span className="min-w-0 flex-1">{address}</span>
                </div>
              )}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="mt-2 flex items-center gap-2.5 text-[14px] text-[#c39e6d]"
                >
                  <Phone size={16} weight="bold" className="shrink-0" />
                  {phone}
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="mt-2 flex items-center gap-2.5 break-all text-[14px] text-[#c39e6d]"
                >
                  <EnvelopeSimple size={16} weight="bold" className="shrink-0" />
                  {email}
                </a>
              )}
              {!address && !phone && !email ? (
                <p
                  data-testid="mobile-job-no-contact"
                  className="mt-4 border-t border-zinc-800 pt-4 text-[11px] font-semibold uppercase tracking-widest text-zinc-600"
                >
                  No customer contact on file
                </p>
              ) : (
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-zinc-800 pt-4">
                  {address && !isFab && (
                    <a
                      href={directionsUrl(address)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="mobile-job-nav"
                      className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-blue-500/40 bg-blue-500/10 text-[10px] font-bold uppercase tracking-wider text-blue-300 hover:bg-blue-500/20"
                    >
                      <NavigationArrow size={14} weight="bold" /> Navigate
                    </a>
                  )}
                  {phone && (
                    <a
                      href={`tel:${phone}`}
                      data-testid="mobile-job-call"
                      className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-[10px] font-bold uppercase tracking-wider text-emerald-300 hover:bg-emerald-500/20"
                    >
                      <Phone size={14} weight="bold" /> Call
                    </a>
                  )}
                  {email && (
                    <button
                      type="button"
                      onClick={() => setEmailDlg(true)}
                      data-testid="mobile-job-email"
                      className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-violet-500/40 bg-violet-500/10 text-[10px] font-bold uppercase tracking-wider text-violet-300 hover:bg-violet-500/20"
                    >
                      <EnvelopeSimple size={14} weight="bold" /> Email
                    </button>
                  )}
                  {phone && !isFab && (
                    <button
                      type="button"
                      onClick={openOnWay}
                      disabled={texting}
                      data-testid="mobile-job-onway"
                      className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-orange-500/40 bg-orange-500/10 text-[10px] font-bold uppercase tracking-wider text-orange-300 hover:bg-orange-500/20 disabled:opacity-40"
                    >
                      <ChatText size={14} weight="bold" /> {texting ? "…" : "Message"}
                    </button>
                  )}
                </div>
              )}
            </Card>

            {job.summary && (
              <Card testId="mobile-job-install-notes">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                  {stopKind} notes
                </div>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-200">
                  {job.summary}
                </p>
              </Card>
            )}

            {files.length > 0 && (
              <Card testId="mobile-job-files-inline">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                    Files · {files.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setTab("docs")}
                    className="text-[11px] font-bold uppercase tracking-widest text-[#c39e6d]"
                  >
                    Manage
                  </button>
                </div>
                <div className="mt-3 space-y-2">
                  {files.slice(0, 5).map((f) => {
                    const nm = f.name || f.filename || f.original_name || "File";
                    const ext = (nm.split(".").pop() || "").toLowerCase();
                    const sz = fmtBytes(f.size ?? f.bytes ?? f.size_bytes);
                    return (
                      <a
                        key={f.id}
                        href={fileUrl(f.storage_path)}
                        target="_blank"
                        rel="noreferrer"
                        download
                        data-testid={`mobile-job-file-inline-${f.id}`}
                        className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-[#080C14] p-3 hover:border-zinc-700"
                      >
                        <span
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${fileTone(ext)}`}
                        >
                          <Files size={16} weight="bold" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-zinc-100">
                            {nm}
                          </span>
                          {sz && <span className="block text-[11px] text-zinc-500">{sz}</span>}
                        </span>
                        <DownloadSimple size={16} weight="bold" className="shrink-0 text-[#c39e6d]" />
                      </a>
                    );
                  })}
                </div>
              </Card>
            )}

            {!isFab && (
            <Card testId="mobile-job-photos-inline">
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                data-testid="mobile-job-photo-input-inline"
                onChange={onPhoto}
              />
              <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="mobile-job-photo-upload-input"
                onChange={onPhoto}
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                  Photos · {photos.length}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="mobile-job-photo-capture"
                    onClick={() => photoRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 rounded-xl border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-orange-300 hover:bg-orange-500/20 disabled:opacity-50"
                  >
                    <Camera size={14} weight="bold" /> Camera
                  </button>
                  <button
                    type="button"
                    data-testid="mobile-job-photo-upload"
                    onClick={() => galleryRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1.5 rounded-xl border border-zinc-700 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-200 hover:bg-zinc-800 disabled:opacity-50"
                  >
                    <UploadSimple size={14} weight="bold" /> Upload
                  </button>
                </div>
              </div>
              {uploading && (
                <p className="mt-2 text-[11px] font-semibold text-[#c39e6d]">Uploading…</p>
              )}
              {photos.length === 0 ? (
                <p className="mt-4 text-center text-sm text-zinc-500">No photos yet.</p>
              ) : (
                <div
                  className="mt-3 grid grid-cols-2 gap-2.5"
                  data-testid="mobile-job-photo-grid-inline"
                >
                  {photos.map((p) => (
                    <div
                      key={p.id}
                      className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-[#080C14]"
                    >
                      <a href={photoUrl(p.storage_path)} target="_blank" rel="noreferrer">
                        <img
                          src={photoUrl(p.thumb_path || p.storage_path)}
                          alt={p.caption || "Job"}
                          className="aspect-[4/3] w-full object-cover"
                        />
                      </a>
                      {canDelete && (
                        <button
                          type="button"
                          data-testid={`mobile-job-photo-delete-${p.id}`}
                          onClick={() => setDelId(p.id)}
                          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-zinc-200 backdrop-blur hover:text-red-400"
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-2.5">
                        {(p.author_name || p.author) && (
                          <div className="flex items-center gap-1 text-[11px] font-bold text-white">
                            <User size={11} weight="bold" /> {p.author_name || p.author}
                          </div>
                        )}
                        <button
                          type="button"
                          data-testid={`mobile-job-photo-caption-${p.id}`}
                          onClick={() => {
                            setCapId(p.id);
                            setCapText(p.caption || "");
                          }}
                          className={`block max-w-full truncate text-left text-[11px] ${
                            p.caption ? "text-zinc-200" : "text-zinc-400"
                          }`}
                        >
                          {p.caption || "+ add note"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {capId && (
                <div className="mt-3 flex items-center gap-2">
                  <input
                    data-testid="mobile-job-caption-input"
                    value={capText}
                    onChange={(e) => setCapText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveCaption()}
                    placeholder="Add a note (e.g. seam at sink)"
                    autoFocus
                    className="h-10 min-w-0 flex-1 rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
                  />
                  <button
                    type="button"
                    data-testid="mobile-job-caption-save"
                    onClick={saveCaption}
                    className="h-10 shrink-0 rounded-xl bg-[#c39e6d] px-4 text-[11px] font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d]"
                  >
                    Save
                  </button>
                </div>
              )}
            </Card>
            )}

            <div className="grid grid-cols-3 gap-3" data-testid="mobile-job-counts">
              {[
                ["Photos", photos.length, Camera, "photos"],
                ["Fixtures", fixtures.length, Drop, "fixtures"],
                ["Files", files.length, Files, "docs"],
              ].filter(([, , , target]) => !(isFab && target === "photos")).map(([label, n, Icon, target]) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`mobile-job-count-${target}`}
                  onClick={() => setTab(target)}
                  className="rounded-2xl border border-zinc-800 bg-[#0F172A] p-3 text-center transition-colors duration-150 hover:border-[#c39e6d]/60 hover:bg-zinc-800 active:scale-[0.98]"
                >
                  <Icon size={18} weight="bold" className="mx-auto text-[#c39e6d]" />
                  <div className="mt-1 text-lg font-bold text-zinc-100">{n}</div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">
                    {label}
                  </div>
                </button>
              ))}
            </div>

            <div data-testid="mobile-job-workorder">
              <MobileQuoteDrawing quoteId={job.quote_id} projectId={job.project_id} />
            </div>

            {job.collect_payment && (
              <Card testId="mobile-job-deposit">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      job.awaiting_deposit ? "bg-amber-400" : job.deposit_paid_at ? "bg-emerald-400" : "bg-zinc-600"
                    }`}
                  />
                  <span className="text-sm text-zinc-200">
                    {job.awaiting_deposit
                      ? "Deposit unpaid"
                      : job.deposit_paid_at
                        ? "Deposit paid"
                        : "No deposit on file"}
                  </span>
                </div>
              </Card>
            )}

            {((job.material_links || []).length > 0 || pieces.length > 0) && (
              <Card testId="mobile-job-materials">
                <div className="text-xs uppercase tracking-wide text-zinc-500">Material / slabs</div>
                {pieces.length > 0 && (
                  <div className="mt-3" data-testid="mobile-job-pieces">
                    <ReservedPieces pieces={pieces} compact testPrefix="mobile-piece" />
                  </div>
                )}
                {(job.material_links || []).some((m) => m.type !== "piece") && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {job.material_links
                      .filter((m) => m.type !== "piece")
                      .map((m, i) => (
                        <span
                          key={i}
                          className="rounded-lg border border-zinc-700 bg-[#080C14] px-2.5 py-1 text-xs text-zinc-200"
                        >
                          {m.color || m.code || m.name || m.label || m.slab_code || "Material"}
                        </span>
                      ))}
                  </div>
                )}
              </Card>
            )}
          </>
        )}

        {/* PROJECT INFO */}
        {tab === "project" && (
          <>
            <Card testId="mobile-job-contacts">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Contact</div>
              <Row label="Name" value={customer?.name || job.customer_name} />
              <Row label="Company" value={customer?.company} />
              <div className="mt-3 flex gap-2 border-t border-zinc-800 pt-3">
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    data-testid="mobile-job-call"
                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
                  >
                    <Phone size={15} weight="bold" /> Call
                  </a>
                )}
                {email && (
                  <a
                    href={`mailto:${email}`}
                    data-testid="mobile-job-email"
                    className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
                  >
                    <EnvelopeSimple size={15} weight="bold" /> Email
                  </a>
                )}
              </div>
              <Row label="Phone" value={phone} />
              <Row label="Email" value={email} />
              <Row label="Address" value={address} />
              {job.customer_id && (
                <Link
                  to={`/customers/${job.customer_id}`}
                  className="mt-3 flex items-center justify-between border-t border-zinc-800 pt-3 text-sm text-[#c39e6d]"
                >
                  Open customer record <CaretRight size={14} weight="bold" />
                </Link>
              )}
            </Card>

            <Card testId="mobile-job-crews">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Assigned crews</div>
              <Row label="Template" value={crewLabel(job.template_crew)} />
              <Row label="Fabrication" value={crewLabel(job.fab_crew)} />
              <Row label="Install" value={crewLabel(job.install_crew)} />
              <Row label="Created by" value={job.created_by} />
            </Card>
          </>
        )}

        {/* COMMUNICATIONS: customer texts + team notes */}
        {tab === "comms" && (
          <>
            {!isFab && (
            <Card testId="mobile-job-messages">
              <div className="text-xs uppercase tracking-wide text-zinc-500">
                Customer texts
              </div>
              <div
                className="mt-3 max-h-[46vh] space-y-2 overflow-y-auto"
                data-testid="mobile-job-msg-thread"
              >
                {messages.length === 0 && (
                  <p className="text-sm text-zinc-500">No texts with this customer yet.</p>
                )}
                {messages.map((m, i) => {
                  const inbound = m.direction === "inbound";
                  return (
                    <div
                      key={m.id || i}
                      data-testid={`mobile-job-msg-${i}`}
                      className={`flex ${inbound ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                          inbound
                            ? "rounded-bl-sm bg-zinc-800 text-zinc-100"
                            : "rounded-br-sm bg-[#c39e6d] text-slate-950"
                        }`}
                      >
                        {m.tag === "running_late" && (
                          <span
                            data-testid={`mobile-job-msg-late-${i}`}
                            className="mb-1 inline-flex items-center gap-1 rounded-md bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-100"
                          >
                            <Clock size={10} weight="bold" /> Running late{m.late_minutes ? ` · +${m.late_minutes} min` : ""}
                          </span>
                        )}
                        <p className="whitespace-pre-line text-sm">{m.body}</p>
                        <div
                          className={`mt-1 text-[10px] ${
                            inbound ? "text-zinc-400" : "text-[#c39e6d]/80"
                          }`}
                        >
                          {inbound ? "Customer" : "Shop"} · {shortTime(m.sent_at)}
                          {m.status && m.status !== "received" && !inbound
                            ? ` · ${m.status}`
                            : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div
                className="mt-3 flex flex-wrap gap-2"
                data-testid="mobile-job-quick-replies"
              >
                {quickReplies.map((q, qi) => (
                  <button
                    key={qi}
                    type="button"
                    data-testid={`mobile-job-quick-${qi}`}
                    onClick={() => sendQuick(renderQuick(q.body, firstName, shopName))}
                    disabled={!phone || msgBusy}
                    className="rounded-full border border-[#c39e6d]/40 bg-[#c39e6d]/10 px-3 py-1.5 text-xs font-semibold text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d]/20 disabled:opacity-40"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 border-t border-zinc-800 pt-3">
                <input
                  data-testid="mobile-job-msg-input"
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendText()}
                  placeholder={phone ? "Text the customer…" : "No customer phone on file"}
                  disabled={!phone || msgBusy}
                  className="h-10 min-w-0 flex-1 rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none disabled:opacity-50"
                />
                <button
                  type="button"
                  data-testid="mobile-job-msg-send"
                  onClick={sendText}
                  disabled={!phone || msgBusy || !msgText.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#c39e6d] text-slate-950 hover:bg-[#c39e6d] disabled:opacity-40"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                </button>
              </div>
            </Card>
            )}

            <Card testId="mobile-job-emails">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Emails sent</div>
              <div className="mt-3 space-y-2" data-testid="mobile-job-email-thread">
                {emails.length === 0 && (
                  <p className="text-sm text-zinc-500">No emails sent yet.</p>
                )}
                {emails.map((em, i) => (
                  <div
                    key={em.id || i}
                    data-testid={`mobile-job-email-${i}`}
                    className="rounded-xl border border-zinc-800 bg-[#0F172A] px-3 py-2"
                  >
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-violet-300">
                      <EnvelopeSimple size={12} weight="bold" /> {em.subject}
                    </div>
                    <p className="mt-1 whitespace-pre-line text-xs text-zinc-400">{em.body}</p>
                    <div className="mt-1 text-[10px] text-zinc-500">
                      To {em.to} · {shortTime(em.sent_at)}
                      {em.sent_by ? ` · ${em.sent_by}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card testId="mobile-job-comms">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Team notes</div>
              <div className="mt-3 flex items-start gap-2">
                <textarea
                  data-testid="mobile-job-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Add a note for the team (staff only)"
                  className="min-w-0 flex-1 resize-none rounded-xl border border-zinc-800 bg-[#080C14] px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="mobile-job-note-add"
                  onClick={addNote}
                  disabled={noteBusy || !note.trim()}
                  className="h-10 shrink-0 rounded-xl bg-[#c39e6d] px-4 text-[11px] font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-40"
                >
                  {noteBusy ? "…" : "Add"}
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {(job.notes || []).length === 0 && (
                  <p className="text-sm text-zinc-500">No team notes yet.</p>
                )}
                {[...(job.notes || [])].reverse().map((n, i) => (
                  <div
                    key={n.id || i}
                    data-testid={`mobile-job-note-${i}`}
                    className="rounded-xl border border-zinc-800 bg-[#080C14] p-3"
                  >
                    <p className="whitespace-pre-line text-sm text-zinc-200">
                      {n.body || n.text || n.note || ""}
                    </p>
                    <div className="mt-1.5 text-[10px] uppercase tracking-wide text-zinc-500">
                      {[n.created_by, n.created_at ? shortTime(n.created_at) : ""]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}

        {/* DOCS & FILES */}
        {tab === "docs" && (
          <Card testId="mobile-job-docs">
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              data-testid="mobile-job-file-input"
              onChange={onFile}
            />
            <button
              type="button"
              data-testid="mobile-job-file-upload"
              onClick={() => fileRef.current?.click()}
              disabled={busyFile}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              <DownloadSimple size={16} weight="bold" className="rotate-180" />
              {busyFile ? "Uploading…" : "Upload a file"}
            </button>
            <div className="mt-3 space-y-2">
              {files.length === 0 && (
                <p className="text-sm text-zinc-500">No files uploaded yet.</p>
              )}
              {files.map((f) => (
                <div
                  key={f.id}
                  data-testid={`mobile-job-file-${f.id}`}
                  className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-[#080C14] p-3"
                >
                  <Files size={18} weight="bold" className="shrink-0 text-zinc-500" />
                  <a
                    href={fileUrl(f.storage_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 truncate text-sm text-zinc-200 hover:text-[#c39e6d]"
                  >
                    {f.name || f.filename || f.original_name || "File"}
                  </a>
                  <button
                    type="button"
                    data-testid={`mobile-job-file-delete-${f.id}`}
                    onClick={() => removeFile(f.id)}
                    className="shrink-0 text-zinc-500 hover:text-red-400"
                  >
                    <Trash size={15} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* PHOTOS / CAMERA */}
        {tab === "photos" && !isFab && (
          <Card testId="mobile-job-photos">
            <input
              ref={photoRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              data-testid="mobile-job-photo-input"
              onChange={onPhoto}
            />
            <button
              type="button"
              data-testid="mobile-job-photo-capture"
              onClick={() => photoRef.current?.click()}
              disabled={uploading}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#c39e6d] text-sm font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-50"
            >
              <Camera size={20} weight="bold" />
              {uploading ? "Uploading…" : "Take / add photo"}
            </button>
            {photos.length === 0 ? (
              <p className="mt-4 text-center text-sm text-zinc-500">No photos yet.</p>
            ) : (
              <div className="mt-4 grid grid-cols-3 gap-2" data-testid="mobile-job-photo-grid">
                {photos.map((p) => (
                  <div key={p.id} className="space-y-1">
                    <div className="relative">
                      <a href={photoUrl(p.storage_path)} target="_blank" rel="noreferrer">
                        <img
                          src={photoUrl(p.thumb_path || p.storage_path)}
                          alt={p.caption || "Job"}
                          className="aspect-square w-full rounded-lg border border-zinc-700 object-cover"
                        />
                      </a>
                      {canDelete && (
                      <button
                        type="button"
                        data-testid={`mobile-job-photo-delete-${p.id}`}
                        onClick={() => setDelId(p.id)}
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-zinc-700 bg-[#080C14] text-zinc-300 hover:text-red-400"
                      >
                        <X size={11} weight="bold" />
                      </button>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid={`mobile-job-photo-caption-${p.id}`}
                      onClick={() => {
                        setCapId(p.id);
                        setCapText(p.caption || "");
                      }}
                      className={`block w-full truncate text-center text-[10px] ${
                        p.caption ? "text-zinc-300" : "text-zinc-600"
                      }`}
                    >
                      {p.caption || "+ note"}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {capId && (
              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid="mobile-job-caption-input"
                  value={capText}
                  onChange={(e) => setCapText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveCaption()}
                  placeholder="Add a note (e.g. seam at sink)"
                  autoFocus
                  className="h-10 min-w-0 flex-1 rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="mobile-job-caption-save"
                  onClick={saveCaption}
                  className="h-10 shrink-0 rounded-xl bg-[#c39e6d] px-4 text-[11px] font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d]"
                >
                  Save
                </button>
              </div>
            )}
          </Card>
        )}

        {/* FIXTURES */}
        {tab === "fixtures" && (
          <>
            {!fxEdit && (
              <button
                type="button"
                data-testid="mobile-job-fixture-add"
                onClick={() => setFxEdit(blankFixture())}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#c39e6d]/40 text-[11px] font-bold uppercase tracking-widest text-[#c39e6d] hover:bg-[#c39e6d]/10"
              >
                <Plus size={16} weight="bold" /> Add sink / fixture
              </button>
            )}

            {fxEdit && (
              <Card testId="mobile-job-fixture-form">
                <div className="text-xs uppercase tracking-wide text-zinc-500">
                  {fxEdit.id ? "Edit fixture" : "New fixture"}
                </div>
                <div className="mt-3 space-y-3">
                  {FIXTURE_FIELDS.map(([key, label, ph]) => (
                    <label key={key} className="block">
                      <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                        {label}
                      </span>
                      <input
                        data-testid={`mobile-job-fixture-field-${key}`}
                        value={fxEdit[key] || ""}
                        placeholder={ph}
                        onChange={(e) =>
                          setFxEdit((f) => ({ ...f, [key]: e.target.value }))
                        }
                        className="mt-1 h-11 w-full rounded-xl border border-zinc-800 bg-[#080C14] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-[#c39e6d] focus:outline-none"
                      />
                    </label>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    data-testid="mobile-job-fixture-cancel"
                    onClick={() => setFxEdit(null)}
                    className="h-11 flex-1 rounded-xl border border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="mobile-job-fixture-save"
                    onClick={saveFixture}
                    disabled={fxBusy}
                    className="h-11 flex-1 rounded-xl bg-[#c39e6d] text-[11px] font-bold uppercase tracking-widest text-slate-950 hover:bg-[#c39e6d] disabled:opacity-40"
                  >
                    {fxBusy ? "Saving…" : "Save fixture"}
                  </button>
                </div>
              </Card>
            )}

            {fixtures.length === 0 && !fxEdit && (
              <Card testId="mobile-job-nofixtures">
                <p className="text-sm text-zinc-500">No fixtures on this job yet.</p>
              </Card>
            )}
            {fixtures.map((fx, i) => (
              <Card key={fx.id || i} testId={`mobile-job-fixture-${i}`}>
                <div className="flex items-center gap-2">
                  <Drop size={16} weight="bold" className="text-[#c39e6d]" />
                  <span className="text-sm font-bold text-zinc-100">
                    {fx.sink || fx.piece || "Fixture"}
                  </span>
                  {fx.warning && (
                    <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                      !
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`mobile-job-fixture-edit-${i}`}
                      onClick={() => setFxEdit({ ...blankFixture(), ...fx })}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      type="button"
                      data-testid={`mobile-job-fixture-delete-${i}`}
                      onClick={() => deleteFixture(fx.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-700 text-zinc-400 hover:text-red-400"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 border-t border-zinc-800 pt-2">
                  <Row label="Area" value={fx.area} />
                  <Row label="Piece" value={fx.piece} />
                  <Row label="Mount" value={fx.mount} />
                  <Row label="Faucet holes" value={fx.faucet_holes} />
                  <Row label="Accessory holes" value={fx.accessory_holes} />
                  {fx.sink_detail?.name && (
                    <Row label="Sink" value={fx.sink_detail.name} />
                  )}
                  {fx.sink_detail?.location && (
                    <Row label="Sink location" value={fx.sink_detail.location} />
                  )}
                  {fx.sink_detail?.reserved && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#c39e6d]/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#c39e6d]">
                      Sink reserved{fx.sink_detail.backorder ? " · backorder" : ""}
                    </div>
                  )}
                  <Row label="Notes" value={fx.notes} />
                  {fx.photo?.storage_path && (
                    <a
                      href={photoUrl(fx.photo.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`mobile-job-fixture-photo-${i}`}
                      className="mt-2 block"
                    >
                      <img
                        src={photoUrl(fx.photo.thumb_path || fx.photo.storage_path)}
                        alt="Fixture reference"
                        className="h-40 w-full rounded-xl border border-zinc-800 object-cover"
                      />
                    </a>
                  )}
                  {(fx.tags || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {fx.tags.map((t, n) => (
                        <span
                          key={n}
                          className="rounded-full border border-zinc-700 bg-[#080C14] px-2 py-0.5 text-[10px] text-zinc-300"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  {fx.warning && (
                    <p className="mt-2 text-xs text-amber-300">{fx.warning}</p>
                  )}
                </div>
              </Card>
            ))}
            {job.sink_notes && (
              <Card testId="mobile-job-sinknotes">
                <div className="text-xs uppercase tracking-wide text-zinc-500">Sink notes</div>
                <p className="mt-2 whitespace-pre-line text-sm text-zinc-300">{job.sink_notes}</p>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
